import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
    Button,
    WeezHeader,
    CopyToClipboardLink,
    Card,
    Icon,
    Table,
    Tooltip,
    ToastContext,
    Message,
    Modal,
    ContentCard,
    TableCellDropdown,
    FilterSetController,
    SubCard,
    // Numbers,
    Dropdown,
    Paginator
} from '@weezevent/nacre';
import Subscription from '../../../models/subscription';
import SubscriptionLinkModel from '../../../models/subscriptionLink';
import { useTranslation } from 'react-i18next';
import LoadingTable from '../../../components/loadingTable';
// import LabelDetails from '../../attendee/detail/components/label-details/labelDetails';
import DeleteModal from './components/deleteModal';
import Styles from './managementRulesList.module.css';
import { SyncButton } from '../../../components/sync-button/sync-button';

function HeaderRow() {
    const { t } = useTranslation();

    return (
        <Table.Tr isHeader>
            <th>{t('ticket.webservice.management-rules.header.name')}</th>
            <th>{t('ticket.webservice.management-rules.header.destination')}</th>
            <th>{t('ticket.webservice.management-rules.header.session')}</th>
            <th>{t('ticket.webservice.management-rules.header.rate_group')}</th>
            <th>{t('ticket.webservice.management-rules.header.conditions')}</th>
            <th>{t('ticket.webservice.management-rules.header.attendees')}</th>
            <th className={Styles['width-x-small']}>{t('ticket.webservice.management-rules.header.actions')}</th>
        </Table.Tr>
    );
}

function TableRow({ link, onSelectManagementRule, organizationId, subscriptionId, history }) {
    const { t } = useTranslation();

    const goToEvent = React.useMemo(() => {
        return (
            <a href={`/ticket/O${organizationId}/E${link.event_id}/dashboard`} target="_blank" rel="noopener noreferrer">
                <Icon name="external-link" />
            </a>
        );
    }, [link, organizationId]);

    // TODO: V2
    const displayCondition = React.useMemo(() => {
        if (!link.ticket_filter) {
            return '-';
        }
        for (const [key, value] of Object.entries(link.ticket_filter)) {
            return `${key}: ${value}`;
        }
    }, [link]);

    // TODO ADRIEN : make expand on list of subscriptionLink to get information about event, category(group), date_id (session)
    // And to add number of attendee send with the link
    return (
        <Table.Tr>
            <Table.Td>{link.name}</Table.Td>
            <Table.Td>
                <div>
                    {link.event_name || link.event_id || '-'}
                    <span className={Styles['external-link']}>{goToEvent}</span>
                </div>
            </Table.Td>
            <Table.Td>{link.date_id || '-'}</Table.Td>
            <Table.Td>{link.category_name || link.category_id || '-'}</Table.Td>
            <Table.Td>{displayCondition || '-'}</Table.Td>
            <Table.Td>{link.tickets_count || '-'}</Table.Td>
            <TableCellDropdown>
                {/* <Dropdown.Item
                    item={t('ticket.common.action.edit')}
                    onClick={() => {
                        return history.push(`/ticket/O${organizationId}/webservices/${subscriptionId}/subscription_links/${link.pk}`);
                    }}
                />
                <Dropdown.Line /> */}
                <Dropdown.Item item={t('ticket.common.action.delete')} deleteItem onClick={() => onSelectManagementRule(link)} />
            </TableCellDropdown>
        </Table.Tr>
    );
}
TableRow.propTypes = {
    link: PropTypes.object,
    history: PropTypes.string.isRequired,
    onSelectManagementRule: PropTypes.func.isRequired,
    organizationId: PropTypes.string.isRequired,
    subscriptionId: PropTypes.string.isRequired
};

export default function ManagementRules({ organizationId, subscriptionId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [filter, setFilter] = React.useState({ search: '' });
    const [selectedManagementRule, setSelectedManagementRule] = React.useState(null);
    const [openModalResyncSub, setOpenModalResyncSub] = React.useState(false);

    const [currentPage, setCurrentPage] = React.useState(0);
    const [defaultPagination] = React.useState(() => ({ limit: 100, offset: 0 }));
    const [queryParams, setQueryParams] = React.useState({ search: '', ...defaultPagination });

    const [subscription, loadingSubscription] = Subscription.useApiModel(
        {
            cache: false,
            allow_cache: false,
            organizationId: organizationId,
            id: subscriptionId,
            query: queryParams
        },
        [organizationId, subscriptionId, queryParams]
    );

    const [subscriptionsLink, loadSubscriptionsLink, sync] = SubscriptionLinkModel.useApiModel(
        {
            cache: false,
            allow_cache: false,
            organizationId: organizationId,
            subscriptionId: subscriptionId,
            query: queryParams
        },
        [organizationId, subscriptionId, queryParams]
    );

    const resyncSubscription = React.useCallback(() => {
        Subscription.push_tickets({ organizationId, subscriptionId: subscription.pk })
            .then(() => {
                toast.success(t('ticket.webservice.management-rules.toast_sync_success'));
                setOpenModalResyncSub(false);
            })
            .catch(() => {
                toast.error(t('ticket.common.toast_error'));
            });
    }, [subscription, organizationId, toast]);

    const onCloseModal = React.useCallback(() => {
        setSelectedManagementRule(null);
    }, []);

    const totalCount = React.useMemo(() => {
        if (loadSubscriptionsLink) {
            return 0;
        }
        return Number.parseInt(subscriptionsLink?.__meta?.headers.get('total-count')) || 0;
    }, [subscriptionsLink, loadSubscriptionsLink]);

    const handlePage = React.useCallback(
        value => {
            setCurrentPage(value);
            setQueryParams({ ...queryParams, offset: queryParams.limit * value });
        },
        [queryParams]
    );

    let onChangeFilter = _filter => {
        let paramChange = new Set();
        paramChange = { ...paramChange, search: _filter.search };
        setQueryParams({ ...queryParams, ...paramChange });
    };

    return (
        <div className={Styles['management-rules-container']}>
            <Modal
                title={t('ticket.webservice.management-rules.modal-sync-sub.title')}
                onClose={() => setOpenModalResyncSub(false)}
                open={openModalResyncSub}
                submitButton={<Button primary onClick={() => resyncSubscription()} label={t('ticket.common.action.confirm')} />}
                cancelButton={<Button onClick={() => setOpenModalResyncSub(false)} label={t(`ticket.common.action.cancel`)} />}
            >
                <Message title={t('ticket.webservice.management-rules.modal-sync-sub.warning-card.title')} type="danger">
                    <p>{t('ticket.webservice.management-rules.modal-sync-sub.warning-card.text')}</p>
                </Message>
            </Modal>

            <WeezHeader
                title={t('ticket.webservice.management-rules.title')}
                rightComponent={[
                    <Tooltip key="sync-tooltip" triggerOnHover position={'bottom'} title={t('ticket.webservice.management-rules.sync-tooltip')}>
                        <SyncButton sync={() => setOpenModalResyncSub(true)} />,
                    </Tooltip>,
                    <Button
                        key="top-cta-form"
                        primary
                        label={
                            <div className={Styles['header-button']}>
                                <Icon name="plus" />
                                <div>{t('ticket.webservice.management-rules.cta-add')}</div>
                            </div>
                        }
                        onClick={() => history.push(`/ticket/O${organizationId}/webservices/${subscriptionId}/subscription_links/new`)}
                    />
                ]}
            />
            <Message type="warning">{t('ticket.webservice.management-rules.warning-message')}</Message>
            <SubCard title={subscription?.name}>
                {/* TODO: V2 */}
                {/* <div className={Styles['card-infos']}>
                    <LabelDetails title={t('ticket.common.attendee')}>
                        <Numbers className={Styles['total-attendees']} value={10 || 0} showNullDecimal />
                    </LabelDetails>
                </div> */}
                <div className={Styles['card-url']}>
                    <CopyToClipboardLink
                        copyOnIconClick
                        delay={1000}
                        tooltipMini
                        value={`${window.weezConfig.api.apiUrl}/ticket-imports/organizations/${organizationId}/hooks/${subscription?.hook_uuid}`}
                        tooltip={t('ticket.common.action.copy')}
                    >
                        <>
                            <span className={Styles['url-title']}>{t('ticket.webservice.management-rules.url')}</span>
                            <span
                                className={Styles['url']}
                            >{`${window.weezConfig.api.apiUrl}/ticket-imports/organizations/${organizationId}/hooks/${subscription?.hook_uuid}`}</span>
                        </>
                    </CopyToClipboardLink>
                </div>
            </SubCard>
            <Card>
                <ContentCard>
                    <FilterSetController
                        total={subscriptionsLink ? subscriptionsLink.length : '--'}
                        labelTotal={t('ticket.webservice.management-rules.total', { count: subscriptionsLink?.length })}
                        labelReset={t('ticket.common.action.reset')}
                        search={{
                            onChange: (synth, value) => {
                                let _filter = { ...filter, search: value };
                                setFilter(_filter);
                                onChangeFilter(_filter);
                            },
                            handleReset: () => {
                                let _filter = { ...filter, search: '' };
                                setFilter(_filter);
                                onChangeFilter(_filter);
                            },
                            displayResetIcon: true,
                            placeholder: t('ticket.common.action.search'),
                            value: filter.search || ''
                        }}
                        onChange={() => {}}
                    />
                </ContentCard>
                <Table useNew className={Styles['attendees_table']}>
                    <Table.THead>
                        <HeaderRow />
                    </Table.THead>
                    {!loadingSubscription && !loadSubscriptionsLink && (
                        <>
                            <DeleteModal
                                subscription={selectedManagementRule}
                                show={Boolean(selectedManagementRule)}
                                onClose={onCloseModal}
                                sync={sync}
                                organizationId={organizationId}
                                subscriptionId={subscriptionId}
                            />
                            <Table.TBody>
                                {subscriptionsLink.map((link, index) => (
                                    <TableRow
                                        key={index}
                                        link={link}
                                        subscriptionId={subscriptionId}
                                        organizationId={organizationId}
                                        onSelectManagementRule={setSelectedManagementRule}
                                        history={history}
                                    />
                                ))}
                            </Table.TBody>
                        </>
                    )}
                </Table>
                <LoadingTable loading={loadingSubscription || loadSubscriptionsLink} />
                {!subscriptionsLink?.length && (
                    <Card.EmptyState
                        title={t('ticket.webservice.management-rules.no-rules')}
                        subtitle={t('ticket.webservice.management-rules.no-rules-info')}
                        icon={<Icon name="user" />}
                        labelButton={t('ticket.webservice.management-rules.no-rules-cta')}
                        onClick={() => history.push(`/ticket/O${organizationId}/webservices/${subscriptionId}/subscription_links/new`)}
                    />
                )}
                <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
                    <Paginator currentPage={currentPage} onPageChange={handlePage} pageSize={queryParams.limit} totalCount={totalCount} />
                </div>
            </Card>
        </div>
    );
}
ManagementRules.propTypes = {
    organizationId: PropTypes.string.isRequired,
    subscriptionId: PropTypes.string.isRequired
};
